import React, { ReactNode } from 'react';

import { Telemetry } from '../hooks/useTelemetry';
import { CriticalApplicationError } from '../types/errors';
import ErrorPage from './ErrorPage';

// A client 500 error boundary represents an error caught late enough that the whole page crashes
// and a user can't work around the error
class Client500 extends React.Component<{ children: ReactNode }> {
  state: { hasError: boolean } = {
    hasError: false,
  };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      const appVersion = process.env.NEXT_PUBLIC_VERSION || 'unknown';
      // Create a CriticalApplicationError with the caught error as inner error
      const criticalError = new CriticalApplicationError(
        `Application render error: ${error.message}`,
        'client',
        error,
      );

      // Everything worth knowing about the ORIGINAL error travels in Faro's
      // `context`, never on criticalError.stack: Faro's stack-trace parser
      // keeps only `at ...` frame lines and discards the rest, so an
      // "Inner Error:" stack trailer loses its header and silently merges
      // the inner frames into the wrapper's own. `context` is passed through
      // verbatim and the parser never looks at it. See types/errors.ts.
      const errorContext = {
        appVersion,
        innerErrorMessage: error.message,
        innerErrorStack: error.stack ?? '',
        // React's component-tree path to the failing render. Previously only
        // console.error'd below and never actually reported anywhere.
        componentStack: errorInfo.componentStack ?? '',
      };

      if (typeof window == 'undefined') {
        // Required inline, not imported: this is the SSR branch, and Next's
        // client compiler substitutes `typeof window` -> 'object', so the
        // whole branch (and the server-only OTel deps behind it) is
        // dead-code-eliminated from the browser bundle.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { noticeServerError } = require('../utils/serverTelemetry');
        noticeServerError(criticalError, errorContext);
      } else {
        Telemetry()?.noticeError(criticalError, errorContext);
      }

      // Log additional context for debugging
      // eslint-disable-next-line no-console
      console.error('Critical application error:', criticalError);
      // eslint-disable-next-line no-console
      console.error('Component stack:', errorInfo.componentStack);
    } catch (err) {}
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage />;
    }

    // All is good
    return this.props.children;
  }
}

export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
): React.ComponentType<P> => {
  const WrappedComponent = (props: P) => (
    <Client500>
      <Component {...props} />
    </Client500>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName ||
    Component.name})`;

  return WrappedComponent;
};

export default Client500;
